/**
 * Module Federation Configuration
 *
 * == BUILD TIME (this file) ==
 * Provides remote config to webpack's ModuleFederationPlugin.
 * This is required because webpack generates the container reference code
 * at compile time for both client and server bundles.
 *
 * == RUNTIME (AWS / config server pipeline) ==
 * On AWS, remote URLs are dynamically resolved from the config server,
 * following the same pipeline as featureFlags and preferences:
 *
 *   config server → getMicrositeSettings() → mergeCommonToDevice
 *     → Context → Html.jsx → window.__PRELOADED_STATE__.context.moduleFederation.remotes.client
 *
 * == How it works ==
 *
 * Client remotes use a runtime promise that reads window.__PRELOADED_STATE__.context.moduleFederation.remotes.client
 * (injected by Html.jsx from configserver data) BEFORE falling back to the
 * build-time settings.json default. This means the remoteEntry URL is
 * decided at runtime, not at build time.
 *
 * Server remotes keep static name@url format because:
 *   a) The server-side @module-federation/enhanced runtime (getMfeComponent.js)
 *      receives its URLs from the bootstrap → preloadMfeComponents() pipeline,
 *      which already uses the configserver-resolved moduleFederation context.
 *   b) The webpack ModuleFederationPlugin server remotes are only compile-time
 *      plumbing so import('remote/Module') resolves in server code.
 *
 * settings.json is ONLY for local development (no config server available).
 * On AWS, the config server provides the real remote URLs at runtime
 * via window.__PRELOADED_STATE__.context.moduleFederation.remotes.client (client) and preloadMfeComponents() (server).
 */

let remotes;
try {
    // __runtime/settings.js pattern: settings.json is only for local dev
    // On AWS, config server overrides at runtime
    const settings = require('../src/settings.json');
    remotes = settings.moduleFederation?.common?.remotes
        || settings.moduleFederation?.remotes
        || { client: {}, server: {} };
} catch (e) {
    remotes = { client: {}, server: {} };
}

/**
 * Extracts the raw URL from a "name@url" string.
 * @param {string} value - "name@url" format string
 * @returns {string} The URL part only
 */
const extractUrl = (value) => {
    if (!value || typeof value !== 'string') return value;
    const parts = value.split('@');
    return parts.length > 1 ? parts.slice(1).join('@') : value;
};

/**
 * Client remotes are resolved lazily in the browser via a promise external so
 * the remoteEntry URL is decided at runtime (configservice / window.__PRELOADED_STATE__.context.moduleFederation.remotes.client)
 * instead of being baked into the bundle at build time.
 *
 * window.__PRELOADED_STATE__.context.moduleFederation.remotes.client is injected by yoda-app-shell's Html component
 * from the configserver-resolved settings. The build-time settings.json URL is
 * only a fallback for environments with no configserver (local dev); when it is
 * used outside local dev that is a config gap, so it is logged loudly rather than
 * silently pointing production at a test CDN.
 */
const buildClientRemote = (name, fallbackUrl) => `promise new Promise(function (resolve, reject) {
    var runtimeRemotes = window.__PRELOADED_STATE__?.context?.moduleFederation?.remotes?.client || {};
    var raw = runtimeRemotes['${name}'];
    if (!raw) {
        raw = '${fallbackUrl}';
        console.warn('[MFE] no runtime remote for ${name} in context.moduleFederation - falling back to build-time URL ' + raw + '. Check configserver moduleFederation for this microsite.');
    }
    var url = raw.indexOf('@') !== -1 ? raw.split('@').slice(1).join('@') : raw;
    if (window['${name}']) { resolve(window['${name}']); return; }
    var script = document.createElement('script');
    script.src = url;
    script.async = true;
    script.onload = function () {
        if (window['${name}']) { resolve(window['${name}']); }
        else { reject(new Error('[MFE] Container ' + '${name}' + ' missing after loading ' + url)); }
    };
    script.onerror = function () { reject(new Error('[MFE] Failed to load remote entry ' + '${name}' + ' from ' + url)); };
    document.head.appendChild(script);
})`;

const getClientRemoteConfig = () => {
    const raw = remotes.client || remotes;
    return Object.entries(raw).reduce((acc, [name, value]) => {
        acc[name] = buildClientRemote(name, extractUrl(value));
        return acc;
    }, {});
};

const getServerRemoteConfig = () => remotes.server || remotes;

const sharedConfig = {
    singleton: true,
    requiredVersion: false,
    eager: true,
    strictVersion: false
};

const getSharedConfig = () => ({
    react: sharedConfig,
    'react-dom': sharedConfig,
    'react-redux': sharedConfig,
    redux: sharedConfig,
    // Host half of the router singleton contract. The federated header subtree
    // uses withRouter/<Link>; without this the remote gets its own react-router
    // and those components find no Router context.
    'react-router': sharedConfig,
    'react-router-dom': sharedConfig,
    // Host half of the RestClient singleton contract. app-shell client.jsx
    // calls restClient.setContext() on this instance; sharing it with the
    // federated remote prevents the remote's getCookie() from crashing on an
    // uninitialized RestClient copy (getContext() returns undefined).
    'yoda-interfaces': sharedConfig,
    // Both remotes share redux-saga as an eager singleton; without the host
    // half every page downloads a second copy inside the remote entry.
    'redux-saga': sharedConfig,
    '@babel/runtime': { singleton: true, requiredVersion: false }
});

module.exports = {
    getClientRemoteConfig,
    getServerRemoteConfig,
    getSharedConfig,
};
